import React from 'react';
import BaseProps from '../../../types/BaseProps';
import Button from 'react-bootstrap/Button';
import AsyncSelect from 'react-select/async';

import {components, Theme} from 'react-select';
import {SearchConcept, SearchTerms} from '../../../types/Search';
import {Maybe} from "../../../types/Maybe";
import classNames from "classnames";
import debounce from "lodash/debounce";

import './SearchField.scss';

type SearchFieldProps = BaseProps & {
  searchTerms: SearchTerms
  onSearch: (searchTerms: SearchTerms) => void;
  onFetchSuggestions: (inputValue: string, callback: (options: Array<SearchFieldOption>) => void) => void;
  disabled: boolean
};

type SearchFieldState = {
  searchTerms: SearchTerms;
}

/**
 * Wrapper component for the search field.
 */
class SearchField extends React.Component<SearchFieldProps, SearchFieldState> {
  constructor(props: any) {
    super(props);
    this.state = {
      searchTerms: {
        freeText: this.props.searchTerms.freeText,
        concept: this.props.searchTerms.concept
      }
    };
    this.onChange = this.onChange.bind(this);
    this.triggerSearch = this.triggerSearch.bind(this);
    this.onSearchButtonClick = this.onSearchButtonClick.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onChange(concept: Maybe<ConceptOption>, freeTextTerms: Array<SelectOption>): void {
    this.setState({
      searchTerms: {
        freeText: freeTextTerms.map((term: SelectOption) => term.value),
        concept: concept
      }
    }, () => this.onSearchFieldClear())
  }

  onSearchFieldClear(): void {
    if (!this.state.searchTerms.concept && !this.state.searchTerms.freeText?.length) {
      this.triggerSearch();
    }
  }

  onSearchButtonClick(): void {
    this.triggerSearch();
  }

  triggerSearch(): void {
    const searchTerms = {
      freeText: this.state.searchTerms.freeText,
      concept: this.state.searchTerms.concept
    };
    this.props.onSearch(searchTerms);
  }

  /**
   * Triggered when the focus of the search input field is removed. It will add to the free text terms, any input that
   * the client wrote, but did not add explicitly by pressing `Enter` or selecting suggestion.
   *
   * @param term to be added to the search arguments
   */
  onBlur(term: string): void {
    if (term) {
      const searchTerms = {
        freeText: [...this.state.searchTerms.freeText, term],
        concept: this.state.searchTerms.concept
      };
      this.setState({searchTerms});
    }
  }

  render() {
    const classes = classNames("SearchFieldWrapper", this.props.className);
    return (
      <div className={classes}>
        <MultiSelectSearchField
          onFetchSuggestions={this.props.onFetchSuggestions}
          onValueSelect={this.onChange}
          onTriggerSearch={this.triggerSearch}
          onBlur={this.onBlur}
          disabled={this.props.disabled}
        />
        <Button className="SearchButton btn btn-primary" disabled={this.props.disabled}
                onClick={this.onSearchButtonClick}>
          Search
        </Button>
      </div>
    );
  }
}

export default SearchField;

// 250 milliseconds
const DEBOUNCE_TIME = 250;

type MultiSelectSearchFieldProps = BaseProps & {
  onFetchSuggestions: (inputValue: string, callback: (options: Array<SearchFieldOption>) => void) => void;
  onValueSelect: (concept: Maybe<ConceptOption>, freeText: Array<SelectOption>) => void;
  onTriggerSearch: () => void;
  onBlur: (value: string) => void;
  disabled: boolean
};

type MultiSelectSearchFieldState = {
  inputValue: string;
  selected: Array<SearchFieldOption>;
};

const selectTheme = (theme: any): Theme => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    primary: '#80bdff'
  }
});

const selectStyles = {
  control: (props: any, state: any) => ({
    ...props,
    borderRadius: 0,
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : ''
  }),
  multiValue: (styles: any, p: any) => {
    const conceptColors = p.data.id ? {
      backgroundColor: '#ed4f2f',
    } : {};
    return {
      ...styles,
      ...conceptColors,
    };
  },
  multiValueLabel: (styles: any, p: any) => {
    const conceptColors = p.data.id ? {
      color: 'white'
    } : {};
    return {
      ...styles,
      ...conceptColors,
    };
  }
};

class MultiSelectSearchField extends React.Component<MultiSelectSearchFieldProps, MultiSelectSearchFieldState> {
  constructor(props: MultiSelectSearchFieldProps) {
    super(props);
    this.state = {
      inputValue: '',
      selected: []
    };
  }

  getCreateLabel(inputValue: string): string {
    return `Free text: ${inputValue}`;
  }

  // When another concept is selected, the first is overwritten
  onChange = (options: any): void => {
    const conceptOptions: Array<ConceptOption> = options.filter((option: SearchFieldOption) => "id" in option);
    const concept = conceptOptions[conceptOptions.length - 1];
    const freeTextTerms: Array<SelectOption> = options.filter((option: SearchFieldOption) => !("id" in option));
    this.setState({selected: [concept, ...freeTextTerms]});
    this.props.onValueSelect(concept, freeTextTerms);
  };

  handleInputChange = (inputValue: string): void => {
    this.setState({inputValue});
  };

  handleKeyDown = (event: any): void => {
    if (event.key === 'Enter' && this.state.inputValue === '') {
      this.props.onTriggerSearch();
    }
  };

  // De-bounce the suggestion fetching to avoid bombing the search service
  fetchSuggestions = debounce(this.props.onFetchSuggestions, DEBOUNCE_TIME);

  /**
   * Triggered when the focus from the input filed is removed. It adds any input which the client wrote, but didn't
   * added explicitly. The additional input is added as free text term to the input.
   */
  onBlur = (): void => {
    const input = this.state.inputValue;
    if (input) {
      const {selected} = this.state;
      const existingValue = selected.filter((option: SearchFieldOption) => !!option)
        .find((option: SearchFieldOption) => option.value === input);
      if (!existingValue) {
        this.setState({selected: [...selected, {label: this.getCreateLabel(input), value: input}]});
        this.props.onBlur(input);
      }
    }
  };

  render() {
    const {inputValue} = this.state;
    const Option = ({children, data, ...props}: any) => {
      return (
        <components.Option {...props}>
          <div>
            <div className='concept-type' style={{float: 'right'}}>{data.type}</div>
            {children}
            <div className='text-muted concept-id'>{data.id}</div>
          </div>
        </components.Option>
      );
    };

    return (
      <AsyncSelect
        styles={selectStyles}
        theme={selectTheme}
        inputValue={inputValue}
        isMulti
        autoFocus
        isDisabled={this.props.disabled}
        className="SearchFieldInput"
        classNamePrefix="SearchFieldInput"
        formatCreateLabel={this.getCreateLabel}
        loadOptions={this.fetchSuggestions}
        onChange={this.onChange}
        onInputChange={this.handleInputChange}
        onBlur={this.onBlur}
        placeholder="Search term..."
        aria-label="Search term..."
        onKeyDown={this.handleKeyDown}
        components={{Option}}
        value={this.state.selected}
      />
    );
  }
}

export type SelectOption = {
  value: string;
  label: string;
};

export type ConceptOption = SelectOption & SearchConcept;

export type SearchFieldOption = SelectOption | ConceptOption;
